import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { setSelectedMarketplaceTab } from "../redux/uiSlice";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
// import { makePaymentForGiftCard } from "../redux/giftBrandsSlice";

type PaymentMethod = "stripe" | "crypto" | "ndeipi";
type CryptoNetwork = "polygon" | "solana";

interface CheckoutFormProps {}
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const CheckoutForm: React.FC<CheckoutFormProps> = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("stripe");
  const [cryptoNetwork, setCryptoNetwork] = useState<CryptoNetwork>("polygon");
  const [walletAddress, setWalletAddress] = useState("");
  const dispatch = useAppDispatch();
  const { giftBrands } = useAppSelector((state) => state.shoppingCart);
  const { chainType } = useAppSelector((state) => state.wallet);
  const { user } = useAppSelector((state) => state.user);
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);

  const total = giftBrands
    .reduce((total, item) => total + item.amount * item.quantity, 0)
    .toFixed(2);

  const handlePaymentMethodChange = (
    event: React.MouseEvent<HTMLElement>,
    newMethod: PaymentMethod
  ) => {
    if (newMethod !== null) {
      setPaymentMethod(newMethod);
    }
  };

  const handleNdeipiPaymentSubmit = () => {
    // Placeholder for payment handling logic
    console.log("Ndeipi payment processing...");
    console.log(`Paying $${total} for product ID ${giftBrands[0].productId}`);
    if (paymentMethod === "ndeipi") {
      /*dispatch(
        makePaymentForGiftCard({
          price: Number(total),
          productId: giftBrands[0].productId,
        })
      );*/
    } else if (paymentMethod === "crypto") {
    } else {
    }
  };

  const handleStripeSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      return;
    }

    try {
      // Create payment intent on your server
      const { clientSecret } = await fetch("/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: Number(total) * 100 }), // Amount in cents
      }).then((res) => res.json());

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError("Card information is incomplete.");
        setLoading(false);
        return;
      }

      const { error: stripeError } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message || "Payment failed");
        setSuccess(false);
      } else {
        setSuccess(true);
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCryptoSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!walletAddress) {
      setError("Please enter your wallet address");
      setLoading(false);
      return;
    }

    try {
      // Simulate crypto payment processing
      // connect to a crypto payment processor here
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Add crypto payment logic
      // For example, connecting to WalletConnect or Phantom wallet
      // and sending the transaction
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Crypto payment failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit =
    paymentMethod === "stripe"
      ? handleStripeSubmit
      : paymentMethod === "crypto"
      ? handleCryptoSubmit
      : handleNdeipiPaymentSubmit;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton
          onClick={() => dispatch(setSelectedMarketplaceTab("cart"))}
          sx={{ mr: 1 }}
          size={isMobileView ? "small" : "medium"}
        >
          <ArrowBackIcon fontSize={isMobileView ? "small" : "medium"} />
        </IconButton>

        <Typography variant="h6" gutterBottom>
          Checkout ({giftBrands.length} items)
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle1" gutterBottom>
        Total: ${total}
      </Typography>

      <ToggleButtonGroup
        value={paymentMethod}
        exclusive
        onChange={handlePaymentMethodChange}
        fullWidth
        sx={{ mb: 3 }}
      >
        <ToggleButton value="stripe">Credit Card</ToggleButton>
        <ToggleButton value="crypto">Crypto</ToggleButton>
        <ToggleButton value="ndeipi">Ndeipi</ToggleButton>
      </ToggleButtonGroup>

      {paymentMethod === "stripe" ? (
        <>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </>
      ) : paymentMethod === "crypto" ? (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Network</InputLabel>
            <Select
              value={cryptoNetwork}
              label="Network"
              onChange={(e) =>
                setCryptoNetwork(e.target.value as CryptoNetwork)
              }
            >
              {chainType === "evm" && (
                <MenuItem value="polygon">Polygon (MATIC)</MenuItem>
              )}
              {chainType === "solana" && (
                <MenuItem value="solana">Solana (SOL)</MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Your Wallet Address"
            variant="outlined"
            value={user?.walletAddress || walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {cryptoNetwork === "polygon"
              ? "You will be prompted to confirm the MATIC transaction"
              : "You will be prompted to confirm the SOL transaction"}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ndeipi payment method is currently not implemented.
        </Typography>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {success && (
        <Typography color="success.main" sx={{ mb: 2 }}>
          Payment Successful!
        </Typography>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={loading || (paymentMethod === "stripe" && !stripe)}
        sx={{ mt: 2 }}
      >
        {loading ? (
          <CircularProgress size={24} />
        ) : paymentMethod === "stripe" ? (
          `Pay $${total} with Credit Card`
        ) : paymentMethod === "crypto" ? (
          `Pay with ${cryptoNetwork === "polygon" ? "MATIC" : "SOL"}`
        ) : (
          `Pay with Ndeipi`
        )}
      </Button>
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        onClick={() => dispatch(setSelectedMarketplaceTab("cart"))}
        sx={{ mt: 2 }}
      >
        Cancel Checkout
      </Button>
    </Box>
  );
};

export default CheckoutForm;
